import { create } from 'zustand';
import { endpoints } from '@/api';
import { ApiError } from '@/api/errors';
import { chatErrorMessage } from '@/api/reasons';
import type { ChallengeView, Message } from '@/api/types';
import { fromServer, oldestId, upsertMessages, type ChatMessage } from '@/features/chat/model';
import { uuid } from '@/lib/id';
import { useAuth } from './auth';

export interface Conversation {
  messages: ChatMessage[]; // ascending
  hasMore: boolean;
  loaded: boolean;
  loading: boolean;
  loadingOlder: boolean;
  error: unknown;
  /** Friend's read position, learned from `chat.read`. */
  peerReadUpTo: number;
  /** My read position we've reported to the server. */
  myReadUpTo: number;
  /** Friend is typing until this epoch ms. */
  typingUntil: number;
  /** Unfriended / blocked: conversation is hidden for both (SPEC_V2 §5). */
  hidden: boolean;
}

const EMPTY: Conversation = {
  messages: [],
  hasMore: false,
  loaded: false,
  loading: false,
  loadingOlder: false,
  error: null,
  peerReadUpTo: 0,
  myReadUpTo: 0,
  typingUntil: 0,
  hidden: false,
};

export type SendResult = { ok: true } | { ok: false; error: string; restore: boolean };

interface ChatState {
  convs: Record<string, Conversation>;
  /** Conversation currently on screen (drives unread + read receipts). */
  active: string | null;
  setActive: (id: string | null) => void;
  get: (id: string) => Conversation;
  load: (id: string) => Promise<void>;
  loadOlder: (id: string) => Promise<void>;
  send: (id: string, body: string, clientId?: string) => Promise<SendResult>;
  retry: (id: string, clientId: string) => Promise<SendResult>;
  discard: (id: string, clientId: string) => void;
  unsend: (id: string, messageId: number) => Promise<void>;
  receive: (m: Message) => void;
  peerRead: (id: string, upTo: number) => void;
  markRead: (id: string, upTo: number) => Promise<void>;
  typing: (id: string, until: number) => void;
  hide: (id: string) => void;
  /** v3: a challenge changed (my guess, or chat.challenge_updated). */
  patchChallenge: (id: string, view: ChallengeView) => void;
  reset: () => void;
}

/** Validation-type failures shouldn't be retried as-is: give the text back to the composer. */
function isRuleError(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 422 || e.status === 409 || e.status === 403 || e.status === 404);
}

let tempId = 0;

/** The server rejects tabs and control characters (newlines are fine). */
export function cleanBody(raw: string): string {
  // eslint-disable-next-line no-control-regex
  return raw.replace(/\t/g, ' ').replace(/[\u0000-\u0009\u000B-\u001F\u007F]/g, '').trim();
}

export const useChat = create<ChatState>((set, get) => {
  const patch = (id: string, p: Partial<Conversation> | ((c: Conversation) => Partial<Conversation>)) =>
    set((s) => {
      const cur = s.convs[id] ?? EMPTY;
      const next = typeof p === 'function' ? p(cur) : p;
      return { convs: { ...s.convs, [id]: { ...cur, ...next } } };
    });

  const post = async (id: string, msg: ChatMessage): Promise<SendResult> => {
    try {
      const saved = await endpoints.sendMessage(id, msg.body, msg.client_id);
      patch(id, (c) => ({ messages: upsertMessages(c.messages, [fromServer(saved)]) }));
      return { ok: true };
    } catch (e) {
      const error = chatErrorMessage(e);
      if (e instanceof ApiError && e.status === 404) patch(id, { hidden: true });
      if (isRuleError(e)) {
        // Remove the optimistic bubble; the composer gets the text back.
        patch(id, (c) => ({ messages: c.messages.filter((m) => m.client_id !== msg.client_id || m.status === 'sent') }));
        return { ok: false, error, restore: true };
      }
      patch(id, (c) => ({ messages: c.messages.map((m) => (m.client_id === msg.client_id && m.status !== 'sent' ? { ...m, status: 'failed', error } : m)) }));
      return { ok: false, error, restore: false };
    }
  };

  return {
    convs: {},
    active: null,
    setActive: (active) => set({ active }),
    get: (id) => get().convs[id] ?? EMPTY,

    load: async (id) => {
      const cur = get().get(id);
      if (cur.loading) return;
      patch(id, { loading: true, error: null });
      try {
        const r = await endpoints.messages(id);
        patch(id, (c) => ({
          messages: upsertMessages(c.messages, (r.messages ?? []).map(fromServer)),
          hasMore: !!r.has_more,
          loaded: true,
          loading: false,
          hidden: false,
        }));
      } catch (e) {
        const hidden = e instanceof ApiError && e.status === 404;
        patch(id, { loading: false, error: e, hidden });
      }
    },

    loadOlder: async (id) => {
      const cur = get().get(id);
      if (cur.loadingOlder || !cur.hasMore) return;
      const before = oldestId(cur.messages);
      if (!before) return;
      patch(id, { loadingOlder: true });
      try {
        const r = await endpoints.messages(id, before);
        patch(id, (c) => ({ messages: upsertMessages(c.messages, (r.messages ?? []).map(fromServer)), hasMore: !!r.has_more, loadingOlder: false }));
      } catch {
        patch(id, { loadingOlder: false });
      }
    },

    send: async (id, raw, clientId = uuid()) => {
      const me = useAuth.getState().me;
      const body = cleanBody(raw);
      const msg: ChatMessage = {
        id: -++tempId,
        connection_id: id,
        sender_id: me?.id ?? '',
        body,
        created_at: new Date().toISOString(),
        deleted: false,
        client_id: clientId,
        status: 'sending',
      };
      patch(id, (c) => ({ messages: upsertMessages(c.messages, [msg]) }));
      return post(id, msg);
    },

    retry: async (id, clientId) => {
      const msg = get().get(id).messages.find((m) => m.client_id === clientId && m.status === 'failed');
      if (!msg) return { ok: true };
      patch(id, (c) => ({ messages: c.messages.map((m) => (m === msg ? { ...m, status: 'sending', error: undefined } : m)) }));
      return post(id, { ...msg, status: 'sending' });
    },

    discard: (id, clientId) => patch(id, (c) => ({ messages: c.messages.filter((m) => !(m.client_id === clientId && m.status !== 'sent')) })),

    unsend: async (id, messageId) => {
      const saved = await endpoints.unsendMessage(id, messageId);
      patch(id, (c) => ({ messages: upsertMessages(c.messages, [fromServer({ ...saved, deleted: true, body: '' })]) }));
    },

    receive: (m) => {
      const id = m.connection_id;
      const cur = get().convs[id];
      // Only fold into conversations we hold; others load fresh when opened.
      if (!cur?.loaded) return;
      const myId = useAuth.getState().me?.id;
      patch(id, (c) => ({
        messages: upsertMessages(c.messages, [fromServer(m)]),
        // A message ends the "typing…" indicator.
        typingUntil: m.sender_id !== myId ? 0 : c.typingUntil,
      }));
    },

    peerRead: (id, upTo) => patch(id, (c) => ({ peerReadUpTo: Math.max(c.peerReadUpTo, upTo) })),

    markRead: async (id, upTo) => {
      const cur = get().get(id);
      if (upTo <= cur.myReadUpTo) return;
      patch(id, { myReadUpTo: upTo });
      try {
        await endpoints.readChat(id, upTo);
      } catch {
        patch(id, (c) => ({ myReadUpTo: Math.min(c.myReadUpTo, cur.myReadUpTo) }));
      }
    },

    typing: (id, until) => patch(id, { typingUntil: until }),
    hide: (id) => patch(id, { hidden: true }),
    patchChallenge: (id, view) =>
      patch(id, (c) => ({ messages: c.messages.map((m) => (m.challenge?.id === view.id ? { ...m, kind: 'challenge', challenge: { ...m.challenge, ...view, guesses: view.guesses ?? [] } } : m)) })),
    reset: () => set({ convs: {}, active: null }),
  };
});

// ---- typing (client → server), throttled to 1 per 3 s per conversation (SPEC_V2 §6)
const lastTyping = new Map<string, number>();
export const TYPING_THROTTLE_MS = 3000;
export function shouldSendTyping(id: string, now = Date.now()): boolean {
  const last = lastTyping.get(id);
  if (last !== undefined && now - last < TYPING_THROTTLE_MS) return false;
  lastTyping.set(id, now);
  return true;
}
export function resetTypingThrottle(): void {
  lastTyping.clear();
}
